import { useMemo, useState } from 'react'
import { useComptes, useCreateDepense, useDeleteDepense, useDepenses, useParametre } from '../api/hooks'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })

export default function Depenses() {
  const depQ = useDepenses()
  const comptesQ = useComptes()
  const param = useParametre(2026)
  const create = useCreateDepense()
  const del = useDeleteDepense()

  const [form, setForm] = useState({
    date: '2026-07-25',
    compte_id: 0,
    montant: '',
    libelle: '',
  })

  const totalParCompte = useMemo(() => {
    const m = new Map<number, number>()
    depQ.data?.forEach(d => m.set(d.compte_id, (m.get(d.compte_id) ?? 0) + Number(d.montant)))
    return m
  }, [depQ.data])

  const total = depQ.data?.reduce((s, d) => s + Number(d.montant), 0) ?? 0

  if (!comptesQ.data) return <p className="text-ocean-700 italic">Chargement…</p>

  return (
    <div className="space-y-4">
      <section className="card p-4 sm:p-5">
        <h2 className="font-display text-lg sm:text-xl font-bold text-ocean-900 mb-1">
          🛒 Dépenses de courses
        </h2>
        <p className="text-xs sm:text-sm text-ocean-700 mb-4">
          Saisis qui a payé quoi et à quelle date. Le montant est réparti automatiquement
          au prorata des repas pris (voir onglet Repas).
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            const montant = parseFloat(form.montant.replace(',', '.'))
            if (isNaN(montant) || montant <= 0) return
            create.mutate(
              {
                date: form.date,
                compte_id: form.compte_id || comptesQ.data![0].id,
                montant,
                libelle: form.libelle,
              },
              { onSuccess: () => setForm({ ...form, montant: '', libelle: '' }) },
            )
          }}
          className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3"
        >
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              required
              value={form.date}
              min={param.data?.date_debut}
              max={param.data?.date_fin}
              onChange={e => setForm({ ...form, date: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Famille payeuse</label>
            <select
              required
              value={form.compte_id || comptesQ.data[0]?.id}
              onChange={e => setForm({ ...form, compte_id: Number(e.target.value) })}
              className="input"
            >
              {comptesQ.data.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Montant (€)</label>
            <input
              type="text"
              inputMode="decimal"
              required
              value={form.montant}
              onChange={e => setForm({ ...form, montant: e.target.value })}
              placeholder="86,32"
              className="input"
            />
          </div>
          <div className="col-span-2 lg:col-span-1">
            <label className="label">Libellé</label>
            <input
              type="text"
              value={form.libelle}
              onChange={e => setForm({ ...form, libelle: e.target.value })}
              placeholder="Courses Leclerc"
              className="input"
            />
          </div>
          <button type="submit" className="btn-coral lg:self-end">
            + Ajouter
          </button>
        </form>
      </section>

      <section className="card overflow-hidden">
        {/* Desktop */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="table-clean">
            <thead>
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Famille</th>
                <th className="text-left px-4 py-2">Libellé</th>
                <th className="text-right px-4 py-2">Montant</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {(depQ.data ?? []).map(d => {
                const c = comptesQ.data.find(x => x.id === d.compte_id)
                return (
                  <tr key={d.id}>
                    <td className="px-4 py-2 text-ocean-700">{fmtDate(d.date)}</td>
                    <td className="px-4 py-2 font-medium text-ocean-900">{c?.nom ?? '—'}</td>
                    <td className="px-4 py-2 text-ocean-700">{d.libelle || <span className="italic opacity-60">—</span>}</td>
                    <td className="px-4 py-2 text-right font-bold text-ocean-900">{fmtEur(Number(d.montant))}</td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => del.mutate(d.id)} className="btn-danger" aria-label="Supprimer">🗑️</button>
                    </td>
                  </tr>
                )
              })}
              {(depQ.data?.length ?? 0) === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-ocean-500 italic">Aucune dépense pour l'instant 🥖</td></tr>
              )}
            </tbody>
            <tfoot className="bg-coral-50">
              <tr>
                <td colSpan={3} className="px-4 py-2 font-bold text-coral-800">Total des dépenses</td>
                <td className="px-4 py-2 text-right font-bold text-coral-800">{fmtEur(total)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile */}
        <div className="sm:hidden divide-y divide-ocean-50">
          {(depQ.data ?? []).map(d => {
            const c = comptesQ.data.find(x => x.id === d.compte_id)
            return (
              <div key={d.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-ocean-900 truncate">{c?.nom}</span>
                    <span className="text-[11px] text-ocean-600">{fmtDate(d.date)}</span>
                  </div>
                  {d.libelle && <div className="text-xs text-ocean-700 truncate">{d.libelle}</div>}
                </div>
                <div className="font-bold text-ocean-900">{fmtEur(Number(d.montant))}</div>
                <button onClick={() => del.mutate(d.id)} className="btn-icon hover:bg-coral-50" aria-label="Supprimer">🗑️</button>
              </div>
            )
          })}
          {(depQ.data?.length ?? 0) === 0 && (
            <div className="px-4 py-6 text-center text-ocean-500 italic">Aucune dépense 🥖</div>
          )}
          {(depQ.data?.length ?? 0) > 0 && (
            <div className="bg-coral-50 px-4 py-3 flex items-center justify-between">
              <span className="font-bold text-coral-800">Total</span>
              <span className="font-bold text-coral-800">{fmtEur(total)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Récap par famille */}
      {Array.from(totalParCompte.entries()).filter(([, v]) => v > 0).length > 0 && (
        <section className="card p-4 sm:p-5">
          <h3 className="font-display font-bold text-ocean-900 mb-3">Payé par famille</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {comptesQ.data
              .filter(c => (totalParCompte.get(c.id) ?? 0) > 0)
              .sort((a, b) => (totalParCompte.get(b.id)!) - (totalParCompte.get(a.id)!))
              .map(c => (
                <div key={c.id} className="bg-ocean-50/60 rounded-lg px-3 py-2">
                  <div className="text-xs text-ocean-700 truncate">{c.nom}</div>
                  <div className="font-bold text-ocean-900">{fmtEur(totalParCompte.get(c.id) ?? 0)}</div>
                </div>
              ))}
          </div>
        </section>
      )}
    </div>
  )
}
